import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Star, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../utils/api';

export const ReviewDialog = ({ open, onClose }) => {
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [text, setText] = useState('');
  const [existing, setExisting] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.getMyReview().then(({ review }) => {
      setExisting(review);
      if (review) { setRating(review.rating); setText(review.text); }
    }).catch(() => {});
  }, [open]);

  const submit = async () => {
    if (!rating) { toast.error('Pick a star rating first'); return; }
    if (text.trim().length < 10) { toast.error('Please write at least 10 characters'); return; }
    setSaving(true);
    try {
      const resp = await api.submitReview(rating, text.trim());
      toast.success(resp.message);
      onClose();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="review-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Star className="w-5 h-5 text-amber-400 fill-amber-400" /> Review LinkMonkey
          </DialogTitle>
        </DialogHeader>
        <div className="text-sm text-muted-foreground -mt-2 flex items-start gap-2 flex-wrap">
          <span>Your review appears publicly on linkmonkey.io/reviews once approved.</span>
          {existing && (
            <Badge variant="secondary">
              {existing.status === 'approved' ? 'Currently live' : 'Awaiting approval'}
            </Badge>
          )}
        </div>
        <div className="flex gap-1 justify-center py-2" data-testid="review-star-picker">
          {[1, 2, 3, 4, 5].map(i => (
            <button key={i} onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(0)}
              onClick={() => setRating(i)} data-testid={`review-star-${i}`}>
              <Star className={`w-8 h-8 transition-colors ${i <= (hover || rating) ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}`} />
            </button>
          ))}
        </div>
        <Textarea value={text} onChange={(e) => setText(e.target.value)} maxLength={600} rows={4}
          placeholder="What do you love about LinkMonkey? What has it helped you do?"
          data-testid="review-text-input" />
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground">{text.length}/600</span>
          <Button onClick={submit} disabled={saving} data-testid="review-submit-btn">
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {existing ? 'Update review' : 'Submit review'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ReviewDialog;
